import type { Theme } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type { WithStyles } from '@material-ui/styles'
import type { UpdateableAreaSubstrate } from '@paintscout/util/builder'
import { getObjectLabels, getArea, getAreaSubstrate } from '@paintscout/util/builder'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import React from 'react'
import classnames from 'classnames'
import { getActiveDimension } from './CalculationHelper'
import { CalculationElement, CalculationOperator, CalculationTerm } from '.'

export const styles = (_theme: Theme) => {
  return createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around'
    }
  })
}

export interface CalculationProductHelperProps extends WithStyles<typeof styles> {
  quote?: QuoteDocument
  options: OptionsDocument
  areaKey?: string
  areaSubstrateKey?: string
  unitLabel?: string
  measurementLabel?: string
  rateType?: string
  productIndex: number
}

function CalculationProductHelper({
  classes,
  quote,
  options,
  areaKey,
  areaSubstrateKey,
  unitLabel,
  measurementLabel,
  rateType,
  productIndex
}: CalculationProductHelperProps) {
  const area = getArea({ quote, options, key: areaKey })
  const areaSubstrate = getAreaSubstrate({ quote, options, areaKey, key: areaSubstrateKey }) as UpdateableAreaSubstrate
  const { currency } = getObjectLabels({ options })
  if (!area || !areaSubstrate) {
    return null
  }
  const activeDimension = getActiveDimension(area, areaSubstrate)
  const { products, coats } = areaSubstrate
  const product = products[productIndex]
  const { price, volume, totalPrice } = product

  let coverageValue = 0
  switch (rateType) {
    case 'lnft': {
      coverageValue = product.lnftCoverage?.useCustom ? product.lnftCoverage.custom : product.lnftCoverage.default
      break
    }
    case 'quantity': {
      coverageValue = product.quantityCoverage?.useCustom
        ? product.quantityCoverage.custom
        : product.quantityCoverage.default
      break
    }
    default: {
      coverageValue = product.coverage?.useCustom ? product.coverage.custom : product.coverage.default
      break
    }
  }

  return (
    <div className={classnames(classes.root, 'product-formula')}>
      <CalculationTerm>
        <CalculationTerm strike={totalPrice.useCustom}>
          <CalculationTerm parenthesis={true}>
            <CalculationTerm strike={volume.useCustom} classes={{ root: 'volume-calculation' }}>
              <CalculationElement
                label={area.areaType == 'surface' ? 'Surface' : 'Room'}
                value={activeDimension}
                format={'dimension'}
                sublabel={measurementLabel}
              />
              <CalculationOperator label={'x'} />
              <CalculationElement label={'coats'} value={coats} />
              <CalculationOperator label={rateType === 'quantity' ? 'x' : '÷'} />
              <CalculationElement
                label={'coverage'}
                value={coverageValue}
                sublabel={rateType === 'quantity' ? `${unitLabel}/item` : `${measurementLabel}/${unitLabel}`}
              />
              <CalculationOperator label={'='} />
            </CalculationTerm>
            <CalculationElement
              label={'volume'}
              value={volume.useCustom ? volume.custom : volume.default}
              sublabel={unitLabel}
            />
          </CalculationTerm>
          <CalculationOperator label={'x'} />
          <CalculationElement
            label={'price'}
            value={price.useCustom ? price.custom : price.default}
            format={'price'}
            sublabel={`${currency.symbol}/${unitLabel}`}
          />
          <CalculationOperator label={'='} />
        </CalculationTerm>
        <CalculationElement
          value={totalPrice.useCustom ? totalPrice.custom : totalPrice.default}
          color={'default'}
          format={'price'}
        />
      </CalculationTerm>
    </div>
  )
}

export default withStyles(styles)(CalculationProductHelper)
